//Room.jsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import socket from '../socket';
import { UserContext } from '../components/UserContext';
import Header from '../components/Header';
import RoomOptions from '../components/RoomOptions';
import AlertModal from '../components/AlertModal';
import LeftContent from '../RoomComponents/LeftContent';
import RightContent from '../RoomComponents/RightContent';
import WaitingForHost from '../RoomComponents/WaitingForHost';
import GameArea from '../RoomComponents/GameArea';
import TeamDisplay from '../RoomComponents/TeamDisplay';
import PlayerList from '../RoomComponents/PlayerList';
import ShowRoundResults from '../RoomComponents/ShowRoundResults';
import GameOver from '../RoomComponents/GameOver';
import { ArrowBigRight, ArrowBigLeft } from 'lucide-react';

const Room = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useContext(UserContext);
    const location = useLocation();
    const [gameSyncData, setGameSyncData] = useState(null);
    const createdRoomData = location.state?.createdRoomData || gameSyncData;
    const [countdown, setCountdown] = useState(0);
    const [showSettings, setShowSettings] = useState(true);

    const playerName = user?.username || 'Guest_' + Math.floor(Math.random() * 1000);
    const playerId = user?.user_id || crypto.randomUUID();
    const [role, setRole] = useState("guesser");
    const player = { id: playerId, name: playerName, role: role, score: 0, status: 'waiting' };
    const { roomId } = useParams();
    const [searchParams] = useSearchParams();
    const roomKeyFromUrl = searchParams.get('key');

    const [gameStarted, setGameStarted] = useState(false);
    const [showRoundResults, setShowRoundResults] = useState(false);
    const [gameHistory, setGameHistory] = useState([]);
    const [gameOver, setGameOver] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const [alertMessageModal, setAlertMessageModal] = useState(null);
    const [playerStatus, setPlayerStatus] = useState('');

    const [rightPanelOpen, setRightPanelOpen] = useState(false);
    const sidePanelRef = useRef(null);

    useEffect(() => {
        socket.on('alert-blocked', ({ msg, info }) => {
            setAlertMessageModal({ message: msg, info: info });
            //setAlertMessage(msg);
            setTimeout(() => setAlertMessageModal(null), 3000);
        });

        socket.on('cue-skipped', ({ message }) => {
            setAlertMessage(message);
            setTimeout(() => setAlertMessage(null), 3000);
        });

        socket.on('round-timer-update', ({ remainingTime }) => {
            setCountdown(remainingTime);
        });

        socket.emit('join-room', {
            roomId: roomId,
            playerData: player,
            roomKey: roomKeyFromUrl
        });

        socket.on('game-sync', (gameSyncData) => {
            //console.log('Game Sync Data:', gameSyncData);
            setGameSyncData(gameSyncData);
            setGameStarted(gameSyncData?.gameStarted || false);
            setGameOver(gameSyncData?.gameOver || false);
            setIsLoading(false);
            const currentPlayer = gameSyncData.players.find(p => p.id === playerId);
            if (currentPlayer) {
                setPlayerStatus(currentPlayer.status);
                updateRoleBasedOnStatus(currentPlayer.status, gameSyncData.actorId, gameSyncData.hostId);
            }
            const isWaitingForNextTurn = currentPlayer?.status.includes('waiting-other-team') || currentPlayer?.status.includes('waiting-player');
            const isRoundJustEnded = gameSyncData?.game?.roundStartTime === null && gameSyncData?.game?.currentRound > 1;

            if (isWaitingForNextTurn || isRoundJustEnded) {
                setShowRoundResults(true);
            } else {
                setShowRoundResults(false);
            }

            if (gameSyncData?.gameHistory) {
                setGameHistory(gameSyncData.gameHistory);
            }
        });

        socket.on('join-response', ({ success, fullRoomData, message }) => {
            //console.log('join-response:', fullRoomData);
            if (success) {
                setGameSyncData(fullRoomData);
                const currentPlayer = fullRoomData.players.find(p => p.id === playerId);
                if (currentPlayer) {
                    setPlayerStatus(currentPlayer.status);
                    updateRoleBasedOnStatus(currentPlayer.status, fullRoomData.actorId, fullRoomData.hostId);
                }
                setIsLoading(false);
            } else {
                setAlertMessageModal({ message: message || 'Failed to join the room.', info: 'Error' });
                setIsLoading(false);
                //setAlertMessage(message || 'Failed to join the room.');
                //setTimeout(() => setAlertMessage(null), 3000);
                setTimeout(() => {
                    setAlertMessageModal(null);
                    navigate('/'); // navigate after 3 seconds
                }, 3000);
            }
        });

        socket.on('show-round-results', ({ gameHistory }) => {
            //console.log('gameHistory:', gameHistory);
            setShowRoundResults(true);
            setGameHistory(gameHistory);
        });

        socket.on('game-over', ({ gameOverData }) => {
            //console.log('Game Over Data:', gameOverData);
            setGameHistory(gameOverData);
            setGameOver(gameSyncData?.gameOver || false);
        });

        socket.on('host-changed', ({ message }) => {
            setAlertMessageModal({ message: message || 'You are now the host.', info: 'Host Transferred' });
            setTimeout(() => setAlertMessageModal(null), 3000);
        });

        socket.on('actor-changed', ({ message }) => {
            setAlertMessageModal({ message: message || 'You are now the actor!', info: 'Actor Transferred' });
            setTimeout(() => setAlertMessageModal(null), 3000);
        });

        socket.on('kicked-redirect', ({ message }) => {
            setAlertMessageModal({ message: message || "You have been removed from the room.", info: 'You have been kicked' });
            setTimeout(() => {
                setAlertMessageModal(null);
                navigate('/');
            }, 3000);
        });

        return () => {
            socket.off('alert-blocked');
            socket.off('cue-skipped');
            socket.off('round-timer-update');
            socket.off('game-sync');
            socket.off('join-response');
            socket.off('show-round-results');
            socket.off('game-over');
            socket.off('host-changed');
            socket.off('actor-changed');
            socket.off('kicked-redirect');
        };
    }, [roomId, roomKeyFromUrl, playerId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                sidePanelRef.current &&
                !sidePanelRef.current.contains(event.target)
            ) {
                setRightPanelOpen(false);
            }
        };

        if (rightPanelOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [rightPanelOpen]);


    const updateRoleBasedOnStatus = (status, actorId, currentHostId) => {
        //console.log('Updating role based on status:', status, actorId, createdByHostId);
        if (currentHostId === playerId) {
            setRole('host');
        } else if (status === 'acting') {
            setRole('actor');
        } else if (status === 'guessing') {
            setRole('guesser');
        } else {
            setRole('guesser');
        }
    };

    if (isLoading) {
        return (
            <WaitingForHost message={"🔄 Joining room..."} />
        );
    }

    const handleLoading = (loadingMessage) => {
        return (
            <WaitingForHost message={loadingMessage} />
        );
    };

    const onKickPlayer = (playerToKickId) => {
        socket.emit('kick-player', { roomId, playerToKickId });
    };

    const onTransferHost = (newHostId) => {
        socket.emit('transfer-host', { roomId, newHostId });
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden">
            <Header
                gameSyncData={gameSyncData}
                player={player}
            />

            <div className="flex-1 grid grid-cols-2 grid-rows-[auto_auto] sm:grid-cols-[25%_auto_25%] sm:grid-rows-1 h-full overflow-hidden pt-[80px] place-items-center">
                {/* Column 1: LeftContent */}
                <div className="p-2 flex items-center justify-center h-full row-start-1 col-start-1 sm:col-start-auto sm:row-start-auto">
                    {gameSyncData && (
                        <LeftContent
                            countdown={countdown}
                        />
                    )}
                </div>

                {/* Column 2: Main content */}
                <div className="p-2 col-span-2 row-start-2 flex flex-col justify-center items-center h-full w-full overflow-hidden sm:col-span-1 sm:row-start-auto">
                    {!gameStarted && !gameOver ? (
                        role === 'host' ? (
                            showSettings && (
                                <RoomOptions
                                    playerId={playerId}
                                    gameSyncData={gameSyncData}
                                    createdRoomData={createdRoomData}
                                    onClose={() => setShowSettings(false)}
                                />
                            )
                        ) : (
                            <WaitingForHost message={"Waiting for Host to start the game…"} />
                        )
                    ) : gameStarted && !gameOver ? (
                        showRoundResults &&
                            ['waiting-player', 'waiting-other-team', 'host-waiting-other-team', 'host-waiting-player'].includes(playerStatus) ? (
                            gameHistory && gameHistory.length > 0 ? (
                                <ShowRoundResults
                                    player={player}
                                    gameSyncData={gameSyncData}
                                    gameHistory={gameHistory}
                                    role={role}
                                    gameStarted={gameStarted}
                                />
                            ) : (
                                handleLoading('Loading game results...')
                            )
                        ) : ['guessing', 'acting', 'host-guessing', 'host-acting'].includes(playerStatus) ? (
                            <GameArea
                                gameSyncData={gameSyncData}
                                player={player}
                                alertMessage={alertMessage}
                            />
                        ) : playerStatus && playerStatus.includes('waiting') ? (
                            <WaitingForHost message={"Waiting for the other team to finish their turn..."} />
                        ) : null
                    ) : gameOver ? (
                        gameHistory && gameHistory.length > 0 ? (
                            <GameOver 
                                role={role} 
                                gameSyncData={gameSyncData}
                                player={player}
                                gameHistory={gameHistory} />

                        ) : (
                            handleLoading('Loading game results...')
                        )
                    ) : null}
                </div>

                {/* Column 3: */}
                <div className="p-2 flex items-center justify-center h-full row-start-1 col-start-2 sm:col-start-auto sm:row-start-auto">
                    {gameSyncData && (
                        <RightContent
                            gameSyncData={gameSyncData}
                            playerId={playerId}
                        />
                    )}
                </div>
            </div>
            {alertMessageModal && (
                <AlertModal
                    message={alertMessageModal.message}
                    info={alertMessageModal.info}
                    onClose={() => setAlertMessageModal(null)}
                />
            )}

            {/* Floating Side Panel */}
            <div className="fixed top-[73px] right-0 h-[calc(100%-73px)] z-50 flex" ref={sidePanelRef}>
                {/* Toggle Button */}
                <button
                    onClick={() => setRightPanelOpen(!rightPanelOpen)}
                    className="bg-[#FFCC21] text-white font-bold p-2 rounded-l-2xl shadow-md 
             hover:bg-yellow-400 transition-all self-start 
             opacity-70 hover:opacity-100"
                >
                    {rightPanelOpen ? <ArrowBigRight /> : <ArrowBigLeft />}
                </button>


                {/* Sliding Panel */}
                <div
                    className={`transition-all duration-300 ease-in-out h-full overflow-auto shadow-lg ${rightPanelOpen ? 'w-80' : 'w-0 p-0'
                        }`}
                >
                    {rightPanelOpen && gameSyncData && (
                        !gameOver && gameSyncData?.gameMode === 'team' && gameSyncData.teams ? (
                            <TeamDisplay
                                player={player}
                                gameSyncData={gameSyncData}
                                role={role}
                                playerStatus={playerStatus}
                                onKickPlayer={onKickPlayer}
                                onTransferHost={onTransferHost}
                            />
                        ) : (
                            <PlayerList
                                player={player}
                                gameSyncData={gameSyncData}
                                role={role}
                                playerStatus={playerStatus}
                                onKickPlayer={onKickPlayer}
                                onTransferHost={onTransferHost}
                            />
                        )
                    )}
                </div>
            </div>


        </div>

    );

};

export default Room;